/**
 * main.ts
 *
 * Bootstraps Vuetify and other plugins then mounts the App`
 */
// import '@/styles/app.css'
import '@/styles/global.scss';
import 'flag-icons/css/flag-icons.min.css';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import App from '@/App.vue';
import XTooltipDirective from '@/directives/v-xtooltip';
import { AppError } from '@/errors/appError';
import { getWebSocketURL } from '@/helpers';
import { setRemoteDictionaries } from '@/helpers/dictionary';
import { failMessage, successMessage } from '@/helpers/message';
import { initXTooltip } from '@/helpers/xtooltip';
import { registerPlugins } from '@/plugins';
import i18n from '@/plugins/i18n';
import { registerServiceWorker } from '@/pwa/registerServiceWorker';
import authService from '@/services/authService';
import ErrorService from '@/services/errorService';
import { connectWebSocket } from '@/services/webSocketClient';
import { useAppStore } from '@/stores/app';
import { useMenuStore } from '@/stores/menu';
import { useUserStore } from '@/stores/user';

document.title = import.meta.env.VITE_APP_NAME;

const app = createApp(App);

app.config.errorHandler = (err, instance, info) => {
    if (err instanceof AppError) {
        ErrorService.log({ err, info });
    } else {
        console.error('[ErrorService]', err);
    }
};

window.addEventListener('error', (event) => {
    ErrorService.log(event.error);

    event.preventDefault();
});

window.addEventListener('unhandledrejection', (event) => {
    ErrorService.log(event.reason);

    event.preventDefault();
});

registerPlugins(app);

const userStore = useUserStore();
const appStore = useAppStore();
const menuStore = useMenuStore();

const uuid = uuidv4();
appStore.uniqAppId = uuid;

axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

axios.interceptors.request.use(
    (request) => {
        /*request.paramsSerializer = {
          indexes: true
        };*/

        const userStore = useUserStore();
        const appStore = useAppStore();
        const token = userStore.token;
        if (token) {
            request.headers['Authorization'] = 'Bearer ' + token;
        }

        if (!request.params) {
            request.params = {};
        }

        request.params['uniqAppId'] = appStore.uniqAppId;
        request.params['locale'] = window.localStorage.getItem('locale');

        request.params.filter = JSON.stringify(request.params.filter);

        return request;
    },
    (error) => {
        Promise.reject(error);
    }
);

axios.interceptors.response.use(
    (response) => {
        if (response.data.dictionary) {
            setRemoteDictionaries(response.data.dictionary);
        }

        let prevUrl = response.config.url;
        if (
            prevUrl &&
            response.data.message &&
            !(
                prevUrl.includes('authenticate') ||
                // prevUrl.includes('registration') ||
                // prevUrl.includes('verify') ||
                // prevUrl.includes('verify-email') ||
                // prevUrl.includes('reset-password') ||
                // prevUrl.includes('forgot-password') ||
                prevUrl.includes('logout')
            )
        ) {
            let message;
            try {
                message = JSON.parse(response.data.message);
                for (let key in message[1]) {
                    message[1][key] = i18n.global.te(message[1][key]) ? i18n.global.t(message[1][key]) : message[1][key];
                }
            } catch (e) {
                message = response.data.message;
            }

            let msg = Array.isArray(message) ? i18n.global.t(message[0], message[1]) : i18n.global.t(message);

            if (Array.isArray(message) && message[0] === 'common.alreadyExists') {
                const table = message[1]?.table;
                if (table === 'SiteAccountParams') {
                    msg = i18n.global.t('accounts.params.alreadyExists');
                } else if (table === 'SiteParams') {
                    msg = i18n.global.t('bookmaker.params.alreadyExists');
                } else if (table === 'ToolUserParams') {
                    msg = i18n.global.t('toolUsers.params.alreadyExists');
                }
            }

            if (Array.isArray(message) && message[0] === 'common.required') {
                const attribute = message[1]?.attribute;
                if (attribute === 'accountParams.value') {
                    msg = i18n.global.t('accounts.params.valueRequired');
                } else if (attribute === 'accountParams.param') {
                    msg = i18n.global.t('accounts.params.paramRequired');
                } else if (attribute === 'siteParams.value') {
                    msg = i18n.global.t('bookmaker.params.valueRequired');
                } else if (attribute === 'siteParams.param') {
                    msg = i18n.global.t('bookmaker.params.paramRequired');
                } else if (attribute === 'toolUserParams.value') {
                    msg = i18n.global.t('toolUsers.params.valueRequired');
                } else if (attribute === 'toolUserParams.param') {
                    msg = i18n.global.t('toolUsers.params.paramRequired');
                }
            }

            if (Array.isArray(message) && message[0] === 'common.max.string') {
                const attribute = message[1]?.attribute;
                const max = message[1]?.max;
                if (attribute === 'accountParams.value') {
                    msg = i18n.global.t('accounts.params.valueMaxLength', { max });
                } else if (attribute === 'accountParams.param') {
                    msg = i18n.global.t('accounts.params.paramMaxLength', { max });
                } else if (attribute === 'siteParams.value') {
                    msg = i18n.global.t('bookmaker.params.valueMaxLength', { max });
                } else if (attribute === 'siteParams.param') {
                    msg = i18n.global.t('bookmaker.params.paramMaxLength', { max });
                } else if (attribute === 'toolUserParams.value') {
                    msg = i18n.global.t('toolUsers.params.valueMaxLength', { max });
                } else if (attribute === 'toolUserParams.param') {
                    msg = i18n.global.t('toolUsers.params.paramMaxLength', { max });
                }
            }

            response.data.success ? successMessage(msg) : failMessage(msg);
        }

        return response;
    },
    (error) => {
        if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
            return Promise.reject(error);
        }

        if (!error.response) {
            failMessage('Network error');
            ErrorService.log(error);
            return Promise.reject(error);
        }

        const { status, statusText, data, config } = error.response;

        switch (status) {
            case 401:
            case 419:
                authService.logout();
                break;
            case 403:
            case 503:
                if (data.message) {
                    const t = i18n.global.t;
                    failMessage(t(data.message));
                } else {
                    failMessage(statusText);
                }
                break;
            case 404:
            case 500:
                failMessage(statusText);
                break;
            default:
                failMessage(`${status}: ${statusText}`);
                break;
        }

        ErrorService.log({
            url: config?.url,
            method: config?.method,
            status,
            data,
            error,
        });

        return Promise.reject(error.response.data);
    }
);

app.mount('#app');

const token = userStore.token;

if (token) {
    const webSocketUrl = getWebSocketURL(token);
    connectWebSocket(webSocketUrl);

    menuStore.withMenu = true;
}

app.directive('xtooltip', XTooltipDirective);
initXTooltip();
registerServiceWorker();
